import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { leaveCalendarLeaveTypeI18nKey } from "features/leave-calendar/leaveCalendar.constants";
import { fetchLeaveUsage, type LeaveUsage } from "services/leave-requests.service";
import { useLanguage } from "utils/use-language";

interface LeaveUsageLineProps {
  companyId: string;
  /** Changes whenever the person's requests changed, so the days are asked for again. */
  refreshKey: number;
}

/**
 * «2026 m. panaudota: Kasmetinės atostogos 14 d. · Nuotolinis darbas 3 d.», with a hint that
 * the balance is not shown yet. There is no balance because the company's yearly norms are not
 * entered anywhere yet.
 */
export default function LeaveUsageLine({ companyId, refreshKey }: LeaveUsageLineProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const [usage, setUsage] = useState<LeaveUsage | null>(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    let cancelled = false;
    // Without an answer nothing is shown: "nothing used" would be a claim the page cannot back.
    fetchLeaveUsage(companyId, year)
      .then((answer) => !cancelled && setUsage(answer))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [companyId, year, refreshKey]);

  if (!usage) return null;

  const formatDays = new Intl.NumberFormat(language, {
    maximumFractionDigits: 2,
  }).format;
  const usedTypes = usage.byType
    .filter((row) => row.days > 0)
    .map((row) =>
      t("leave_usage_type_days", {
        type: t(leaveCalendarLeaveTypeI18nKey(row.leaveType), { defaultValue: row.leaveType }),
        days: formatDays(row.days),
      })
    );

  return (
    <div>
      <p className="text-base font-semibold text-gray-900">
        {usedTypes.length > 0
          ? t("leave_usage_used", { year: usage.year, types: usedTypes.join(" · ") })
          : t("leave_usage_none", { year: usage.year })}
      </p>
      <p className="mt-0.5 text-sm text-gray-500">{t("leave_usage_no_balance_hint")}</p>
    </div>
  );
}
